<script setup>
import {computed, reactive, onMounted} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {useComponentsStore} from '@/stores/components'
import {useToastsStore} from '@/stores/toasts'
import {useModalsStore} from '@/stores/modals'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'

import FormControlCategoryTitle from '@/common/form-control/FormControlCategoryTitle'
import OrderDetailsCustomerPanel from '@/components/order-details/OrderDetailsCustomerPanel'
import vUserAccess from '@/vue/directives/userAccess'
import FormControlPlainText from "@/common/form-control/FormControlPlainText.vue"
import Button from '@/common/Button.vue'
import ItemStatusIcons from "@/common/ItemStatusIcons.vue"
import {useUserStore} from "@/stores/user"

const props = defineProps({
    formId: String,
    field: Object,
    currentData: Object,
    value: [Number, String]
})

const router = useRouter()
const route = useRoute()

const componentsStore = useComponentsStore()
const toastsStore = useToastsStore()
const modalsStore = useModalsStore()
const itemDataStore = useItemDataStore()
const userStore = useUserStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

const fieldData = reactive({tableName: undefined})

const tableName = computed(() => formStore.value?.tableName)
const id = computed(() => formStore.value?.id)
const isOrderCustomerControl = computed(() => tableName.value === 'm_basket_orders' && props.field.columnName === 'customerId')
const formSmall = computed(() => formStore.value?.formSmall)
const hasAccess = computed(() => !!userStore.access(fieldData.tableName))

const getDefault = () => {
    // Required validation will only work if the hidden input's value is an empty string.
    return props.field.required ? '' : 0
}

const manageCategories = () => {
    if (fieldData.tableName === 'templates_templates') {
        router.push({name: 'themesEditor', query: {section: 'templates_templates'}})
    } else {
        router.push({name: 'list', params: {component: fieldData.tableName}})
    }
}

const editItem = () => {
    if (fieldData.tableName === 'templates_templates') {
        router.push({
            name: 'themesEditor',
            query: {
                section: 'templates_templates',
                id: props.currentData[props.field.name]
            }
        })
    } else {
        router.push({
            name: 'form',
            params: {
                component: fieldData.tableName,
                id: props.currentData[props.field.name]
            }
        })
    }
}

const removeCategory = () => {
    props.currentData[props.field.name] = getDefault()
}

// A category cannot be the child of its own descendant.
// Recursively check the new parent's ancestors to make sure the current scope.id isn't found.
const checkAncestors = async (targetId, callback) => {
    const {parentId} = await itemDataStore.get({
        tableName: tableName.value,
        id: targetId
    })

    if (parentId === id.value) {
        toastsStore.add({
            body: 'A category cannot be the child of its descendant.'
        })
    } else if (parentId) {
        await checkAncestors(parentId, callback)
    } else {
        callback()
    }
}

const parentIdValidation = async (targetId, callback) => {
    if (targetId === id.value) {
        toastsStore.add({
            body: 'A category cannot be its own parent.'
        })
    } else if (targetId) {
        await checkAncestors(targetId, callback)
    }
}

const selectCategories = async () => {
    // Avoids [""] being provided to the select listing
    let selectedIds = []
    let selectedId = props.currentData[props.field.name]

    if (selectedId) {
        selectedIds.push(selectedId)
    }

    const modalObj = modalsStore.show({
        componentName: 'SelectListModal',
        obj: {
            listingName: fieldData.tableName + '.' + props.field.name,
            selectedIds: selectedIds,
            onSelect: (newIds) => {
                let targetId = newIds.pop()

                let setValue = (targetId) => {
                    props.currentData[props.field.name] = targetId
                    if (modalObj?.id !== undefined) {
                        modalsStore.remove(modalObj.id)
                    }
                }

                // Only validate parents and ancestors if the field is referencing the same table.
                if (fieldData.tableName === tableName.value) {
                    parentIdValidation(targetId, () => {
                        setValue(targetId)
                    })
                } else {
                    setValue(targetId)
                }
            }
        }
    })
}

// Emulating asynchronous created hook logic
onMounted(async () => {
    const component = componentsStore.items.find(o => o.id === props.field.categoryComponentId)

    // When this component is manually embedded using a static field array a tableName can be provided.
    fieldData.tableName = props.field.tableName ? props.field.tableName : component?.tableName

    // Set the default
    const currentId = props.currentData[props.field.name]
    let value = getDefault()
    if (currentId) {
        // If an item exists, don't set the default
        const item = await itemDataStore.get({tableName: component?.tableName, id: currentId})
        if (item) return
    } else {
        const idStr = route.query[props.field.name]
        if (idStr) value = parseInt(idStr)
    }

    formStore.value?.presetData({name: props.field.name, value})
})
</script>

<template>
    <div v-if="fieldData.tableName">
        <template v-if="field.readonly">
            <OrderDetailsCustomerPanel v-if="isOrderCustomerControl" :customer-id="value" :order-id="id"/>

            <FormControlPlainText v-else :form-small="formSmall" :form-id="formId">
                <a v-if="hasAccess" href @click.prevent="editItem">
                    <FormControlCategoryTitle :id="currentData[field.name]" :table-name="fieldData.tableName"/>
                </a>
                <FormControlCategoryTitle v-else :id="currentData[field.name]" :table-name="fieldData.tableName"/>
            </FormControlPlainText>
        </template>

        <div v-else>
            <input v-model="currentData[field.name]" :name="field.name" :required="field.required"
                   class="visually-hidden">

            <div class="mb-2">
                <Button @click="selectCategories">
                    <i class="bi-plus-lg"/>
                </Button>

                <Button v-user-access="fieldData.tableName" @click="manageCategories">
                    <i class="bi-pencil-square"/>
                </Button>
            </div>

            <OrderDetailsCustomerPanel v-if="isOrderCustomerControl" :customer-id="value" :order-id="id"/>

            <div v-else-if="currentData[field.name]" class="list-group">
                <a class="list-group-item d-flex align-items-center" href @click.prevent="editItem">
                    <FormControlCategoryTitle :id="currentData[field.name]" :table-name="fieldData.tableName"
                                              class="flex-fill"/>

                    <ItemStatusIcons :id="currentData[field.name]" :table-name="fieldData.tableName"/>

                    <Button @click.stop.prevent="removeCategory">
                        <i class="bi-x-lg"/>
                    </Button>
                </a>
            </div>
        </div>
    </div>
</template>